import { useForm } from "react-hook-form";
import { PapInput, PapErrors } from '../components/FormElements';
import { useState } from "react";
import { Link } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion } from 'framer-motion';
import { KeyRound, Send, CheckCircle2 } from 'lucide-react';
import useApi from "../hooks/useApi";
import AuthLayout from '../components/AuthLayout';

interface ForgotPasswordFormData {
  email: string;
}

const schema = yup.object().shape({
  email: yup.string().email('Please enter a valid email').required('Email is required')
});

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.23, 1, 0.32, 1] as const } },
};

const ForgotPasswordPage: React.FC = () => {
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const [resetSuccess, setResetSuccess] = useState(false);
  const api = useApi();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(schema)
  });

  const resetPassword = async (data: ForgotPasswordFormData) => {
    try {
      const result = await api.post('/api/v1/passwords', data);
      if (result) {
        setPageErrors([]);
        setResetSuccess(true);
      }
    } catch (ex: any) {
      if (ex.response) {
        const exResponseBody = await ex.response.json();
        setPageErrors([exResponseBody.error]);
      }
    }
  };

  return (
    <AuthLayout>
      <motion.div variants={stagger} initial="hidden" animate="show">
        {/* Icon & heading */}
        <motion.div variants={fadeUp} className="text-center mb-8">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-accent/10 flex items-center justify-center mb-4">
            <KeyRound className="w-7 h-7 text-accent" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground mb-1.5">
            Forgot your password?
          </h1>
          <p className="text-sm text-muted-foreground">
            Enter your email and we'll send you a reset link.
          </p>
        </motion.div>

        {/* Errors */}
        {pageErrors.length > 0 && (
          <motion.div variants={fadeUp}>
            <PapErrors errors={pageErrors} />
          </motion.div>
        )}

        {/* Success */}
        {resetSuccess && (
          <motion.div variants={fadeUp}>
            <div className="flex items-center gap-2.5 p-3.5 bg-success/[0.06] dark:bg-success/[0.1] border border-success/20 rounded-xl mb-5 text-sm text-success">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span className="font-medium">Check your email for a password reset link.</span>
            </div>
          </motion.div>
        )}

        {/* Form */}
        <motion.form variants={fadeUp} noValidate onSubmit={handleSubmit(resetPassword)} method="post">
          <PapInput fieldLabel="Email" type="email" formBinding={register("email")} placeholder="" autocomplete={false} fieldName="email" error={errors.email} required={true} />

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary btn-block py-3 text-base font-semibold gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {isSubmitting ? 'Sending...' : 'Send Reset Link'}
          </button>
        </motion.form>

        {/* Back to login */}
        <motion.p variants={fadeUp} className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/login" className="text-accent hover:text-accent/80 hover:underline font-semibold transition-colors">
            Back to Sign In
          </Link>
        </motion.p>
      </motion.div>
    </AuthLayout>
  );
};

export default ForgotPasswordPage;
